import { useState, useEffect } from 'react';
import { bugReportsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Popover, PopoverContent, PopoverTrigger
} from './ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { Bug, Plus, Clock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700'
};

const statusColors = {
  new: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-blue-100 text-blue-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-700',
  wont_fix: 'bg-slate-100 text-slate-700'
};

export const BugTracker = () => {
  const { user, hasRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps_to_reproduce: '',
    expected_behavior: '',
    actual_behavior: '',
    priority: 'medium',
    page_url: ''
  });

  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (isOpen) {
      fetchBugs();
      if (isAdmin) {
        fetchStats();
      }
    }
  }, [isOpen, isAdmin]);

  const fetchBugs = async () => {
    setLoading(true);
    try {
      const res = await bugReportsAPI.getAll();
      setBugs(res.data || []);
    } catch (error) {
      console.error('Failed to fetch bugs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await bugReportsAPI.getStats();
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please provide a title and description');
      return;
    }
    
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        page_url: formData.page_url || window.location.pathname,
        browser_info: navigator.userAgent
      };
      await bugReportsAPI.create(data);
      toast.success('Bug report submitted! Thank you for your feedback.');
      setReportDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        steps_to_reproduce: '',
        expected_behavior: '',
        actual_behavior: '',
        priority: 'medium',
        page_url: ''
      });
      fetchBugs();
      if (isAdmin) fetchStats();
    } catch (error) {
      toast.error('Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (bugId, newStatus) => {
    try {
      await bugReportsAPI.update(bugId, { status: newStatus });
      toast.success('Bug status updated');
      fetchBugs();
      if (isAdmin) fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const newBugsCount = bugs.filter(b => b.status === 'new').length;

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="bug-tracker-btn"
          >
            <Bug className="h-5 w-5" />
            {newBugsCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
                {newBugsCount > 9 ? '9+' : newBugsCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Bug Tracker</h3>
                <p className="text-xs text-muted-foreground">Report and track issues</p>
              </div>
              <Button size="sm" onClick={() => setReportDialogOpen(true)} data-testid="report-bug-btn">
                <Plus className="h-4 w-4 mr-1" /> Report Bug
              </Button>
            </div>
            
            {isAdmin && stats && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                <div className="text-center p-2 bg-slate-50 rounded">
                  <p className="text-lg font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="text-center p-2 bg-purple-50 rounded">
                  <p className="text-lg font-bold text-purple-700">{stats.new}</p>
                  <p className="text-xs text-muted-foreground">New</p>
                </div>
                <div className="text-center p-2 bg-blue-50 rounded">
                  <p className="text-lg font-bold text-blue-700">{stats.in_progress}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
                <div className="text-center p-2 bg-red-50 rounded">
                  <p className="text-lg font-bold text-red-700">{stats.critical_open}</p>
                  <p className="text-xs text-muted-foreground">Critical</p>
                </div>
              </div>
            )}
          </div>
          
          <ScrollArea className="h-72">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : bugs.length > 0 ? (
              <div className="divide-y">
                {bugs.slice(0, 10).map(bug => (
                  <div key={bug.id} className="p-3 hover:bg-slate-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{bug.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{bug.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-xs ${priorityColors[bug.priority]}`}>
                            {bug.priority}
                          </Badge>
                          <Badge className={`text-xs ${statusColors[bug.status]}`}>
                            {bug.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      {isAdmin && bug.status !== 'resolved' && bug.status !== 'closed' && (
                        <Select 
                          value={bug.status} 
                          onValueChange={(v) => handleStatusChange(bug.id, v)}
                        >
                          <SelectTrigger className="w-24 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="wont_fix">Won't Fix</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    {bug.reported_by_name && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Reported by {bug.reported_by_name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 mb-2" />
                <p className="text-sm">No bugs reported</p>
                <p className="text-xs">Click "Report Bug" if you find an issue</p>
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      {/* Report Bug Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-500" />
              Report a Bug
            </DialogTitle>
            <DialogDescription>
              Help us improve by reporting any issues you encounter
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Brief description of the issue"
                data-testid="bug-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the bug in detail"
                rows={3}
                data-testid="bug-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="steps">Steps to Reproduce</Label>
              <Textarea
                id="steps"
                value={formData.steps_to_reproduce}
                onChange={(e) => setFormData(prev => ({ ...prev, steps_to_reproduce: e.target.value }))}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                rows={3}
                data-testid="bug-steps-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expected">Expected Behavior</Label>
                <Textarea
                  id="expected"
                  value={formData.expected_behavior}
                  onChange={(e) => setFormData(prev => ({ ...prev, expected_behavior: e.target.value }))}
                  placeholder="What should happen"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="actual">Actual Behavior</Label>
                <Textarea
                  id="actual"
                  value={formData.actual_behavior}
                  onChange={(e) => setFormData(prev => ({ ...prev, actual_behavior: e.target.value }))}
                  placeholder="What actually happens"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger data-testid="bug-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="page">Page URL</Label>
                <Input
                  id="page"
                  value={formData.page_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, page_url: e.target.value }))}
                  placeholder="Auto-filled with current page"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReportDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-bug-btn">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Bug className="h-4 w-4 mr-2" />}
                Submit Report
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugTracker;
